import React, { FC, ReactNode, useMemo } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import { Icon } from '@dbt-labs/sourdough';

import { InteractionDagNode } from '../../dag/internalDag';
import { DbtDagNode, getLensInfo, useDagContext } from '../../dbt-dag';
import { getResourceNodeColor } from '../../hooks/useResourceNodeColor';
import { useInternalDagContext } from '../../providers/InternalDagProvider';
import { ResourceTypeExplorer } from '../ResourceTypeIcon';

type ZoomedOutNodeDisplayProps = {
  baseClassname: string;
  innerClassname: string;
  resourceType: ResourceTypeExplorer | 'unknown';
  isSelected: boolean;
  isHovered: boolean;
  children: ReactNode;
  showColor: boolean;
};

const ZoomedOutNodeDisplay: FC<ZoomedOutNodeDisplayProps> = ({
  baseClassname,
  innerClassname,
  isSelected,
  children,
  showColor,
}) => {
  return (
    <div
      className={twMerge(
        baseClassname,
        !showColor && 'border border-borderMuted bg-fgVizNeutral text-fgWhite',
        isSelected && '-m-1 border-4',
        'zoom-level-quarter:block',
        'zoom-level-half:block',
        'absolute inset-0 hidden',
      )}
      data-testid="zoomed-out-node-container"
      data-zoomed-out-node-show-color={showColor}
    >
      <div
        className={twMerge(
          'relative w-full overflow-hidden text-ellipsis whitespace-nowrap px-4 text-3xl ',
          'zoom-level-quarter:text-5xl',
          'mt-5 zoom-level-quarter:mt-2 zoom-level-quarter:leading-[1.3]',
          innerClassname,
        )}
        data-testid="zoomed-out-node"
      >
        {children}
      </div>
    </div>
  );
};

type ZoomedOutNodeProps<TDbtDagNode extends DbtDagNode> = {
  baseClassname: string;
  innerClassname: string;
  resourceType: ResourceTypeExplorer | 'unknown';
  isSelected: boolean;
  isHovered: boolean;
  data: InteractionDagNode<TDbtDagNode>;
};

const ZoomedOutNodeContainer = <TDbtDagNode extends DbtDagNode>({
  baseClassname,
  innerClassname,
  resourceType,
  isSelected,
  isHovered,
  data,
}: ZoomedOutNodeProps<TDbtDagNode>) => {
  const { lens, lensContext } = useDagContext();
  const { grain } = useInternalDagContext();

  const nodeBgColor = getResourceNodeColor({
    showColor: true,
    resourceType: data.resourceType,
  });

  const lensInfo = getLensInfo(lens, data, lensContext);

  const showColor = (!!lensInfo || !lens) ?? false;

  const children = useMemo(() => {
    if (grain === 'column') {
      return (
        <>
          <div className="w-full overflow-hidden text-ellipsis zoom-level-quarter:mt-4">
            {data.label}
          </div>
          <div className="mt-2 w-full overflow-hidden text-ellipsis text-2xl zoom-level-quarter:hidden">
            {data.sublabel}
          </div>
        </>
      );
    }
    if (lensInfo?.icon) {
      return (
        <span>
          <Icon
            className={twJoin('zoom-level-quarter:hidden', 'mr-1 mt-2.5')}
            ryecon={lensInfo.icon}
            size="md"
            testId="badge-icon"
          />
          <span>{data.label}</span>
        </span>
      );
    }
    return data.label;
  }, [grain, data.label, data.sublabel, lensInfo?.icon]);

  return (
    <ZoomedOutNodeDisplay
      baseClassname={twMerge(baseClassname, nodeBgColor, lensInfo?.color)}
      innerClassname={innerClassname}
      isHovered={isHovered}
      isSelected={isSelected}
      resourceType={resourceType}
      showColor={showColor}
    >
      {children}
    </ZoomedOutNodeDisplay>
  );
};

export const ZoomedOutNode = React.memo(ZoomedOutNodeContainer);
